import { useEffect, useState, useCallback } from 'react';

/**
 * Idle Detection Hook
 * 
 * Detects when user has been idle (no mouse/keyboard/touch activity)
 * for a specified duration. Useful for pausing background tasks to save battery.
 * 
 * @param idleTimeMs - Milliseconds of inactivity before considered idle (default: 5 minutes)
 * @returns isIdle - true if user is idle, false if active
 */
export function useIdleDetection(idleTimeMs: number = 300000) {
  const [isIdle, setIsIdle] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  const handleActivity = useCallback(() => {
    setLastActivityTime(Date.now());
    if (isIdle) {
      console.log('[IdleDetection] User activity detected, resuming from idle');
      setIsIdle(false);
    }
  }, [isIdle]);

  useEffect(() => {
    // Events that indicate user activity
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

    // Throttle activity detection to avoid too many state updates
    let throttleTimeout: number | null = null;
    const throttledHandleActivity = () => {
      if (throttleTimeout) return;
      throttleTimeout = window.setTimeout(() => {
        handleActivity();
        throttleTimeout = null;
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, throttledHandleActivity, { passive: true });
    });

    // Check idle status periodically
    const checkIdleInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityTime;
      const shouldBeIdle = timeSinceActivity >= idleTimeMs;

      if (shouldBeIdle && !isIdle) {
        console.log('[IdleDetection] User idle detected after', Math.round(timeSinceActivity / 1000), 'seconds');
        setIsIdle(true);
      }
    }, 10000); // Check every 10 seconds

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, throttledHandleActivity);
      });
      clearInterval(checkIdleInterval);
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [lastActivityTime, idleTimeMs, isIdle, handleActivity]);

  return isIdle;
}

